import './styles/selection.scss';
import { create, nextRepaint } from '../../utils/dom';
import {
    SELECTION_CSS_CLASS_ELEMENT_NAME,
    SELECTION_SUSPENDED_CLASS_NAME,
    SELECTION_LENGTHENING_CLASS_NAME,
    SELECTION_SHORTENING_CLASS_NAME,
    SELECTION_IN_BETSLIP_CLASS_NAME,
    SELECTION_HIGHLIGHT_DURATION_IN_MILLI_SECONDS,
    SELECTION_CSS_ID_ATTRIBUTE_NAME,
    SELECTION_CSS_CORRECTSCORE_ID_ATTRIBUTE_NAME,
    SELECTION_CSS_FIRSTGOALSCORER_ID_ATTRIBUTE_NAME,
    SELECTION_CSS_LABEL_ATTRIBUTE_NAME,
    SELECTION_HIDDEN_CLASS_NAME,
    SELECTION_SCORECAST_CLASS_NAME,
    I18N_PREFIX,
    SELECTION_GTM_ADD_BETSLIP,
    SELECTION_GTM_REMOVE_BETSLIP,
} from './constants';
import { SP_ONLY_CODE } from './../sport-event/constants';

import pick from 'lodash/pick';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { getOddsFromSelection, addAmericanOdds } from '../sport-event/utils';
import i18n from './../../utils/i18n';
import { betslipHasSelection } from '~Betslip/interface/storage';
import { onOddsFormatChange } from '~shared/utils/user';

export default class Selection {

    /**
     * Creates a new selection
     * @param {Object} [initialState=null] the initial selection data
     * @param {Boolean} [showLabel=false] If the label should be shown
     * @param {String} [label=''] Optional label
     */
    constructor({
        initialState = null,
        showLabel = false,
        label = '',
        gtmAttribute = '',
        isScorecast = false,
        doTranslateLabel = true,
    } = {}) {
        /**
         * The initial selection state
         * @type {Object}
         */
        this.initialState = initialState;

        /**
         * The current changeable values
         * @type {Object}
         */
        this.currentValues = null;

        /**
         * The properties that are likely to change
         * @type {Array}
         */
        this.changeableProperties = ['suspended', 'decimal_price', 'fraction_price', 'american_price', 'sp_only'];

        this.gtmAttribute = gtmAttribute;

        /**
         * Whether the selection is scorecast or not
         * @type {Boolean}
         */
        this.isScorecast = isScorecast;

        /**
         * Default true.
         * @type {Boolean} if true the label will be translated using teh static files.
         */
        this.doTranslateLabel = doTranslateLabel;

        /**
         * The component dom element
         * @type {Element}
         */
        this.el = create('span', `${SELECTION_CSS_CLASS_ELEMENT_NAME}`);

        if (this.isScorecast) {
            this.el.classList.add(SELECTION_SCORECAST_CLASS_NAME);
        }

        if (showLabel) {
            this.setLabel(label);
        }

        if (!isNull(this.initialState)) {
            this.setIdAttribute();
            this.setLabelAttribute();
            this.setGTMAttribute();

            const betslipId = this.isScorecast ?
                this.generateScorecastId(
                    this.initialState.firstGoalScorerId,
                    this.initialState.correctScoreId) : initialState.id;

            if (betslipHasSelection(betslipId)) {
                this.showAsSelected();
            }
        }

        this.update(initialState);

        // it's far from ideal.
        // there is no way to unsubscribe because each time we render new Selection
        // then new Selection object is created and we have no ability to detect when it's removed.
        // until this is fully migrated to React we need to rely on Garbage Collector.
        onOddsFormatChange(() => {
            this.switchOddsFormat();
        });
    }

    /**
     * Sets the id as an html attribute
     * @return {void}
     */
    setIdAttribute() {
        if (!isUndefined(this.initialState.correctScoreId) && !isUndefined(this.initialState.firstGoalScorerId)) {
            this.el.setAttribute(SELECTION_CSS_CORRECTSCORE_ID_ATTRIBUTE_NAME, this.initialState.correctScoreId);
            this.el.setAttribute(SELECTION_CSS_FIRSTGOALSCORER_ID_ATTRIBUTE_NAME, this.initialState.firstGoalScorerId);
            this.el.setAttribute(SELECTION_CSS_ID_ATTRIBUTE_NAME, this.generateScorecastId(
                this.initialState.firstGoalScorerId,
                this.initialState.correctScoreId
                )
            );
        } else if (this.initialState.id) {
            this.el.setAttribute(SELECTION_CSS_ID_ATTRIBUTE_NAME, this.initialState.id);
        }
    }

    /**
     * To be used with scorecast to update button element when selection is changed.
     * @param {*} state New state of the button.
     * @returns {void}
     */
    updateIdAttribute({correctScoreId, firstGoalScorerId}) {
        if (!isUndefined(correctScoreId) && !isUndefined(firstGoalScorerId)) {
            this.el.setAttribute(SELECTION_CSS_CORRECTSCORE_ID_ATTRIBUTE_NAME, correctScoreId);
            this.el.setAttribute(SELECTION_CSS_FIRSTGOALSCORER_ID_ATTRIBUTE_NAME, firstGoalScorerId);
            this.el.setAttribute(SELECTION_CSS_ID_ATTRIBUTE_NAME, this.generateScorecastId(firstGoalScorerId, correctScoreId));
        }
    }

    /**
     * Sets the label as an html attribute
     * @return {void}
     */
    setLabelAttribute() {
        const marketHeader = this.initialState.marketHeader;

        if (marketHeader) {
            this.el.setAttribute(SELECTION_CSS_LABEL_ATTRIBUTE_NAME, i18n.t(`${I18N_PREFIX}.${marketHeader.toLowerCase()}`, marketHeader));
        }
    }

    /**
     * Sets the gtm tag as a html attribute
     * @return {void}
     */
    setGTMAttribute() {
        const gtmAttribute = this.gtmAttribute;
        if (gtmAttribute) {
            if (this.el.classList.contains(SELECTION_IN_BETSLIP_CLASS_NAME)){
                this.el.setAttribute('data-spectate-product', `${gtmAttribute}${SELECTION_GTM_REMOVE_BETSLIP}`);
            } else {
                this.el.setAttribute('data-spectate-product', `${gtmAttribute}${SELECTION_GTM_ADD_BETSLIP}`);
            }
        }
    }

    /**
     * Updates the selection values
     * @param {Object} updatedState the next selection data
     * @param {Boolean} silent if true do not show the highlighted state
     * @return {void}
     */
    update(updatedState, silent = false) {
        if (isNull(updatedState)) {
            this.suspend();

            return;
        }

        const nextState = addAmericanOdds(updatedState);

        const {firstGoalScorerId, correctScoreId} = updatedState || {};
        if (this.isScorecast && !isUndefined(firstGoalScorerId) && !isUndefined(correctScoreId)) {
            // if isScorecast update the element with new state when selection changes.
            this.updateIdAttribute(updatedState);
            const betslipId = this.generateScorecastId(firstGoalScorerId, correctScoreId);

            if (betslipHasSelection(betslipId)) {
                this.showAsSelected();
            } else {
                this.showAsUnSelected();
            }
        }

        if (isUndefined(nextState.sp_only)) {
            nextState.sp_only = false;
        }

        if (isNull(this.initialState)) {
            this.initialState = nextState;

            this.setIdAttribute();

            if (betslipHasSelection(nextState.id)) {
                this.showAsSelected();
            } else {
                this.showAsUnSelected();
            }
        }

        if (this.shouldUpdateOdds(nextState)) {
            this.setOdds(getOddsFromSelection(nextState));
        }

        if (this.shouldUpdateSuspension(nextState)) {
            if (nextState.suspended) {
                this.suspend();
            } else {
                this.unsuspend();
            }
        }

        const nextValues = pick(nextState, this.changeableProperties);

        if (!silent) {
            this.highlight(nextValues);
        }

        this.initialState.marketHeader = nextState.marketHeader;
        this.setLabelAttribute();
        this.setGTMAttribute();

        this.currentValues = nextValues;
    }

    /**
     * Highlights selection on odds change
     * @param {Object} nextValues the next values for the update
     * @return {void}
     */
    highlight(nextValues) {
        if (!isNull(this.currentValues)) {
            const zero = 0;
            let nextPrice = nextValues.decimal_price;
            let currentPrice = this.currentValues.decimal_price;
            nextPrice = nextPrice === SP_ONLY_CODE ? zero : nextPrice;
            currentPrice = currentPrice === SP_ONLY_CODE ? zero : currentPrice;
            if (nextPrice > currentPrice) {
                this.showAsLengthened();
            } else if (nextPrice < currentPrice) {
                this.showAsShortened();
            }
        }
    }

    /**
     * Set the odds
     * @param {String} odds the odds
     * @return {void}
     */
    setOdds(odds) {
        nextRepaint(() => {
            /**
             * NB: odds should always be passed to this component in raw form, i.e. NOT localised
             *
             */
            const displayOdds = odds === SP_ONLY_CODE ? i18n.t('betslip.sp') : odds;
            this.el.textContent = displayOdds;
        });
    }

    /**
     * Switch Odds Format
     * @return {void}
     */
    switchOddsFormat() {
        if (!isNull(this.currentValues)) {
            this.setOdds(getOddsFromSelection(this.currentValues));
        }
    }

    /**
     * Set the label
     * @param {String} label the label
     * @return {void}
     */
    setLabel(label) {
        const _label = this.doTranslateLabel ? i18n.t(`${I18N_PREFIX}.${label}`) : label;
        this.el.setAttribute(SELECTION_CSS_LABEL_ATTRIBUTE_NAME, _label);
    }

    /**
     * Suspends the selection
     * @return {void}
     */
    suspend() {
        nextRepaint(() => {
            this.el.classList.add(SELECTION_SUSPENDED_CLASS_NAME);
        });
    }

    /**
     * Unsuspends the selection
     * @return {void}
     */
    unsuspend() {
        nextRepaint(() => {
            this.el.classList.remove(SELECTION_SUSPENDED_CLASS_NAME);
        });
    }

    /**
     * Highlights the selection for betting
     * @return {void}
     */
    showAsSelected() {
        nextRepaint(() => {
            this.el.classList.add(SELECTION_IN_BETSLIP_CLASS_NAME);
            this.setGTMAttribute();
        });
    }
    /**
     * Removes the highlighted style.
     * @return {void}
     */
    showAsUnSelected() {
        nextRepaint(() => {
            this.el.classList.remove(SELECTION_IN_BETSLIP_CLASS_NAME);
            this.setGTMAttribute();
        });
    }


    /**
     * Shows the selection as shortened
     * @return {void}
     */
    showAsShortened() {
        nextRepaint(() => {
            this.el.classList.add(SELECTION_SHORTENING_CLASS_NAME);
            setTimeout(() => this.hideAsShortened(), SELECTION_HIGHLIGHT_DURATION_IN_MILLI_SECONDS);
        });
    }

    /**
     * Removes the shortened highlighting
     * @return {void}
     */
    hideAsShortened() {
        nextRepaint(() => {
            this.el.classList.remove(SELECTION_SHORTENING_CLASS_NAME);
        });
    }

    /**
     * Shows the selection as lengthened
     * @return {void}
     */
    showAsLengthened() {
        nextRepaint(() => {
            this.el.classList.add(SELECTION_LENGTHENING_CLASS_NAME);
            setTimeout(() => this.hideAsLengthened(), SELECTION_HIGHLIGHT_DURATION_IN_MILLI_SECONDS);
        });
    }

    /**
     * Removes the lengthened highlighting
     * @return {void}
     */
    hideAsLengthened() {
        nextRepaint(() => {
            this.el.classList.remove(SELECTION_LENGTHENING_CLASS_NAME);
        });
    }

    /**
     * Hides the selection
     * @return {void}
     */
    hide() {
        nextRepaint(() => {
            this.el.classList.add(SELECTION_HIDDEN_CLASS_NAME);
        });
    }

    /**
     * Shows the selection
     * @return {void}
     */
    show() {
        nextRepaint(() => {
            this.el.classList.remove(SELECTION_HIDDEN_CLASS_NAME);
        });
    }

    /**
     * Check if selection suspended status has changed
     * @param  {Object} selection The updated selection
     * @return {Boolean} returns true if the suspended status has changed
     */
    shouldUpdateSuspension(selection) {
        if (this.currentValues === null) {
            return true;
        }

        return this.currentValues.suspended !== selection.suspended;
    }

    /**
     * Check if selection odds have changed
     * @param  {Object} selection The updated selection
     * @return {Boolean} returns true if odds have changed
     */
    shouldUpdateOdds(selection) {
        if (this.currentValues === null) {
            return true;
        }

        return (
            this.currentValues.decimal_price !== selection.decimal_price ||
            this.currentValues.fraction_price !== selection.fraction_price ||
            this.currentValues.sp_only !== selection.sp_only
        );
    }

    /**
     * Generate scorecast id string
     * @param {string} firstGoalScorerId first goal scorer selection id
     * @param {string} correctScoreId  correct score selection id
     * @return {string} return scorecastId
     */
    generateScorecastId(firstGoalScorerId, correctScoreId) {
        return `${firstGoalScorerId}_${correctScoreId}`;
    }

    /**
     * Clears intial state and current values and replaces them with new given state
     * @param {Object} newState new values to use.
     * @returns {void}
     */
    replace(newState) {
        if (this.isScorecast) {
            // Replace should not be called on a scorecast selection because we get the.
            // scorecast information from php and replace is probably only going to be used by
            // websocket updates which will not have this information.
            // eslint-disable-next-line no-console
            console.error('Replace should not be called on scorecast selection.');

            return;
        }
        this.initialState = null;
        this.currentValues = null;
        this.update(newState);
    }
}
